"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import type { AuthUser } from "@/types";
import { identifyUser } from "@/lib/telemetry";
import {
  getAccessToken,
  getTokenExpiry,
  refreshAccessToken,
  storeTokens,
  clearTokens,
} from "@/lib/auth-tokens";

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: () => void;
  logout: () => Promise<void>;
  getSession: () => Promise<AuthUser | null>;
  handleCallback: (code: string, state: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:8787";
const APP_URL = process.env.NEXT_PUBLIC_APP_URL || "http://localhost:3000";
const AUTH0_DOMAIN = process.env.NEXT_PUBLIC_AUTH0_DOMAIN || "";
const AUTH0_CLIENT_ID = process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID || "";
const AUTH0_AUDIENCE = process.env.NEXT_PUBLIC_AUTH0_AUDIENCE || "";

// PKCE helpers for secure OAuth flow
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
}

// Refresh this long before the access token actually expires, so API calls
// never race an expiring token.
const REFRESH_LEEWAY_MS = 60_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Proactively refresh the access token shortly before it expires, so the
  // user never hits a 401 mid-session. Reschedules itself after each
  // successful refresh; on failure the reactive 401 path takes over.
  const scheduleProactiveRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const token = getAccessToken();
    if (!token) return;

    const expiresAt = getTokenExpiry(token);
    if (!expiresAt) return;

    const delay = Math.max(expiresAt - Date.now() - REFRESH_LEEWAY_MS, 5_000);
    refreshTimerRef.current = setTimeout(async () => {
      const refreshed = await refreshAccessToken();
      if (refreshed) scheduleProactiveRefresh();
    }, delay);
  }, []);

  const getSession = useCallback(async (): Promise<AuthUser | null> => {
    setLoading(true);
    setError(null);

    const token = getAccessToken();
    if (!token) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return null;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          setLoading(false);
          return getSession();
        }
        throw new Error("Unauthorized");
      }

      const data = await res.json();

      if (data.data) {
        const authUser: AuthUser = {
          id: data.data.userId,
          email: data.data.email,
          name: data.data.name,
          role: data.data.role,
          orgId: data.data.orgId,
          tenant: data.data.tenant,
          isFirstLogin: data.data.isFirstLogin,
        };
        setUser(authUser);
        setIsAuthenticated(true);
        setLoading(false);
        scheduleProactiveRefresh();
        identifyUser(authUser.id);
        return authUser;
      }

      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return null;
    } catch {
      clearTokens();
      setUser(null);
      setIsAuthenticated(false);
      setError("Session expired. Please log in again.");
      setLoading(false);
      return null;
    }
  }, [scheduleProactiveRefresh]);

  const login = useCallback(async () => {
    const state = generateState();
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Store PKCE verifier and state for validation in callback
    sessionStorage.setItem("pkce_code_verifier", codeVerifier);
    sessionStorage.setItem("oauth_state", state);

    const scope = "openid profile email offline_access";

    const params = new URLSearchParams({
      response_type: "code",
      client_id: AUTH0_CLIENT_ID,
      redirect_uri: `${APP_URL}/callback`,
      scope,
      state,
      audience: AUTH0_AUDIENCE,
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });

    window.location.href = `https://${AUTH0_DOMAIN}/authorize?${params.toString()}`;
  }, []);

  const handleCallback = useCallback(
    async (code: string, state: string) => {
      setLoading(true);
      setError(null);

      try {
        // Validate state to prevent CSRF. A missing saved state means this
        // callback was not initiated by our login() in this browser session
        // (or storage was cleared) — reject rather than trust the URL value.
        const savedState = sessionStorage.getItem("oauth_state");
        if (!savedState || savedState !== state) {
          throw new Error(
            "Sign-in session could not be verified. Please try logging in again."
          );
        }

        // Retrieve PKCE code_verifier
        const codeVerifier = sessionStorage.getItem("pkce_code_verifier");

        const res = await fetch(`${API_BASE_URL}/api/auth/callback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code,
            state,
            redirect_uri: `${APP_URL}/callback`,
            code_verifier: codeVerifier,
          }),
        });
        const data = await res.json();

        // Clean up PKCE and state
        sessionStorage.removeItem("pkce_code_verifier");
        sessionStorage.removeItem("oauth_state");

        if (!res.ok || !data.data?.access_token) {
          throw new Error(data.error?.message || "Failed to exchange authorization code");
        }

        storeTokens(data.data.access_token, data.data.refresh_token);
        await getSession();
      } catch (e: any) {
        setError(e.message || "Authentication failed");
        setLoading(false);
        throw e;
      }
    },
    [getSession]
  );

  const logout = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    try {
      const token = getAccessToken();
      if (token) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // Continue with local cleanup even if API call fails
    }

    clearTokens();
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);

    // Redirect to Auth0 logout
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/config`);
      const data = await res.json();
      const params = new URLSearchParams({
        client_id: data.data.clientId,
        returnTo: APP_URL,
      });
      window.location.href = `https://${data.data.domain}/v2/logout?${params.toString()}`;
    } catch {
      window.location.href = "/";
    }
  }, []);

  // Check session on mount; clear any pending refresh timer on unmount
  useEffect(() => {
    getSession();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [getSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        error,
        login,
        logout,
        getSession,
        handleCallback,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
